import { type ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";

// Fire `onReachBottom` once the scroll gets within this many px of the end, so
// the next page is prefetched before the user hits the exact bottom.
const REACH_BOTTOM_THRESHOLD_PX = 96;
// Re-arm only after scrolling back this far from the end (hysteresis). Larger
// than the fire threshold so loading a page — which shifts the scroll via anchor
// adjustment, oscillating the distance around the threshold — can't re-fire on
// its own. The user must genuinely scroll away and back to load again.
const REARM_DISTANCE_PX = 400;

type FadeScrollProps = {
  children: ReactNode;
  /** Background color that the fades blend into. Defaults to `#fbfbfb`. */
  fadeColor?: string;
  /** Height of each fade overlay in Tailwind units (rem-based). */
  fadeHeight?: string;
  /** Extra classes on the inner scroll container. */
  className?: string;
  /**
   * Called whenever the scroll reaches near the bottom (for infinite scroll).
   * `null` disables it. Reliable inside this nested scroll container, where a
   * viewport-rooted IntersectionObserver is not. Fires repeatedly while near the
   * bottom, so the consumer must guard against re-entrancy.
   */
  onReachBottom: (() => void) | null;
};

function FadeScroll({
  children,
  fadeColor = "#fbfbfb",
  fadeHeight = "h-6",
  className,
  onReachBottom,
}: FadeScrollProps): React.JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showFadeTop, setShowFadeTop] = useState(false);
  const [showFadeBottom, setShowFadeBottom] = useState(false);

  // Keep the latest callback reachable from the listener without re-subscribing.
  const onReachBottomRef = useRef(onReachBottom);
  useEffect(() => {
    onReachBottomRef.current = onReachBottom;
  });

  // Fire once per approach to the bottom, with hysteresis re-arming. Without it,
  // staying near the bottom (loading inserts items and the scroll anchor nudges
  // the distance around the threshold) would fire on every scroll/resize tick
  // and fetch in a loop. `armed` gates a single fire until the user scrolls back.
  const armedRef = useRef(true);

  useEffect(() => {
    const container = scrollRef.current;
    if (container === null) return;

    function updateFades(): void {
      if (container === null) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceToBottom = scrollHeight - (scrollTop + clientHeight);
      setShowFadeTop(scrollTop > 4);
      setShowFadeBottom(distanceToBottom > 4);

      if (distanceToBottom > REARM_DISTANCE_PX) {
        armedRef.current = true;
      }
      if (armedRef.current && distanceToBottom <= REACH_BOTTOM_THRESHOLD_PX) {
        armedRef.current = false;
        const reachBottom = onReachBottomRef.current;
        if (reachBottom !== null) reachBottom();
      }
    }

    updateFades();
    container.addEventListener("scroll", updateFades, { passive: true });
    const resizeObserver = new ResizeObserver(updateFades);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener("scroll", updateFades);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className="relative flex-1 min-h-0">
      <div
        aria-hidden
        style={{
          backgroundImage: `linear-gradient(to bottom, ${fadeColor}, transparent)`,
        }}
        className={cn(
          "pointer-events-none absolute inset-x-0  top-0 z-10",
          "transition-opacity duration-200",
          fadeHeight,
          showFadeTop ? "opacity-100" : "opacity-0"
        )}
      />
      <div
        aria-hidden
        style={{
          backgroundImage: `linear-gradient(to top, ${fadeColor}, transparent)`,
        }}
        className={cn(
          "pointer-events-none absolute inset-x-0  bottom-0 z-10",
          "transition-opacity duration-200",
          fadeHeight,
          showFadeBottom ? "opacity-100" : "opacity-0"
        )}
      />
      <div ref={scrollRef} className={cn("h-full overflow-y-auto", className)}>
        {children}
      </div>
    </div>
  );
}

export { FadeScroll };
